<template>
  <div class="dashboard-container">
    <div
      v-if="loading"
      class="progress-bar"
      :style="{ width: progress + '%' }"
    ></div>

    <div class="mb-3 page-title">
      <i class="bi bi-flag-fill"></i> Pendaftaran Kontingen Turnamen
    </div>

    <!-- Registration Guide Steps -->
    <div class="registration-guide-card mb-4">
      <div class="guide-steps-grid">
        <div class="guide-step is-active">
          <div class="guide-step__num">1</div>
          <div class="guide-step__text">
            <strong>Daftarkan Kontingen</strong>
            <small>Pilih nama kontingen yang Anda sertakan</small>
          </div>
        </div>
        <div class="guide-step">
          <div class="guide-step__num">2</div>
          <div class="guide-step__text">
            <strong>Input Atlet</strong>
            <small>Isi atlet & kelas tanding / seni</small>
          </div>
        </div>
        <div class="guide-step">
          <div class="guide-step__num">3</div>
          <div class="guide-step__text">
            <strong>Buat Tagihan</strong>
            <small>Cetak invoice & konfirmasi pelunasan</small>
          </div>
        </div>
      </div>
    </div>

    <div class="row g-4">
      <!-- Tournament Poster & Info -->
      <div class="col-lg-5">
        <div class="tournament-info-card">
          <img v-if="!loading && tournamentImage" :src="tournamentImage" class="tournament-info-card__img" alt="Poster Turnamen" />
          
          <div class="p-3">
            <div class="d-flex align-items-center gap-3 mb-3">
              <img v-if="tournamentLogo" :src="tournamentLogo" alt="Logo Turnamen" class="shadow-sm" style="width: 56px; height: 56px; object-fit: contain; background: #fff; border-radius: 8px; border: 1px solid #e2e8f0; padding: 4px;" />
              <div>
                <h3 class="tournament-name mb-0">{{ tournamentName }}</h3>
                <span class="badge bg-primary-subtle text-primary border border-primary-subtle small mt-1" v-if="tournamentLogo">Kejuaraan Terverifikasi</span>
              </div>
            </div>
            <p class="text-secondary small mb-3">{{ tournamentDescription }}</p>

            <div class="info-badges-list">
              <div class="info-badge-item" v-if="tournamentDate">
                <i class="bi bi-calendar-event text-accent"></i>
                <div>
                  <small class="text-muted d-block">Jadwal Turnamen</small>
                  <strong>{{ tournamentDate }}</strong>
                </div>
              </div>

              <div class="info-badge-item" v-if="tournamentLocation">
                <i class="bi bi-geo-alt-fill text-danger"></i>
                <div>
                  <small class="text-muted d-block">Lokasi Pertandingan</small>
                  <span v-html="tournamentLocation"></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Contingent Selection & Action Area -->
      <div v-if="!loading" class="col-lg-7">
        <div class="contingent-select-card">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h4 class="fw-bold mb-1">Daftar Kontingen Anda</h4>
              <p class="text-secondary small mb-0">Centang kontingen yang akan diikutsertakan pada turnamen ini.</p>
            </div>
            <router-link to="/admin/contingent/create" class="button button-ghost button-sm">
              <i class="bi bi-plus-lg"></i> Tambah Kontingen
            </router-link>
          </div>

          <form @submit.prevent="submitForm">
            <!-- Contingents List -->
            <div v-if="contingents && contingents.length > 0" class="contingents-box-list">
              <div 
                v-for="(contingent, index) in paginatedContingents" 
                :key="index" 
                class="contingent-item-card"
                :class="{ 'is-registered': contingent.is_registered, 'is-checked': contingent.isSelected }"
              >
                <div class="contingent-item-left">
                  <div class="contingent-checkbox-wrap" v-if="!contingent.is_registered">
                    <input 
                      class="form-check-input"
                      type="checkbox"
                      v-model="contingent.isSelected"
                      :id="`cont-${contingent.id}`"
                    >
                  </div>
                  <div class="contingent-registered-icon" v-else>
                    <i class="bi bi-check-circle-fill text-success fs-5"></i>
                  </div>

                  <label :for="`cont-${contingent.id}`" class="contingent-item-name mb-0">
                    <strong>{{ contingent.name }}</strong>
                    <span v-if="contingent.is_registered" class="badge bg-success-subtle text-success ms-2">
                      Sudah Terdaftar
                    </span>
                    <span v-else class="badge bg-secondary-subtle text-secondary ms-2">
                      Belum Terdaftar
                    </span>
                  </label>
                </div>

                <!-- Action Button if already registered -->
                <div v-if="contingent.is_registered" class="contingent-item-action">
                  <router-link
                    to="/admin/team-members"
                    class="button button-primary button-sm"
                  >
                    <i class="bi bi-person-plus-fill"></i> Kelola Atlet
                  </router-link>
                </div>
              </div>
            </div>

            <!-- No contingent data available -->
            <div v-else class="alert alert-warning d-flex align-items-center gap-2">
              <i class="bi bi-exclamation-triangle-fill fs-5"></i>
              <div>
                Anda belum memiliki kontingen. Silakan buat kontingen terlebih dahulu.
              </div>
            </div>

            <!-- Submit button -->
            <div class="mt-4 pt-2 border-top d-flex justify-content-between align-items-center" v-if="contingents.length > 0">
              <span class="text-muted small">
                {{ jumlahDipilih }} kontingen dipilih
              </span>

              <button 
                type="submit" 
                class="button button-primary" 
                :disabled="loading || jumlahDipilih === 0"
              >
                <i class="bi bi-check-lg"></i>
                <span>Daftarkan Kontingen Terpilih</span>
              </button>
            </div>
          </form>

          <!-- Pagination -->
          <div class="d-flex justify-content-center mt-3" v-if="totalPages > 1">
            <button 
              class="btn btn-sm btn-outline-secondary me-2"
              :disabled="currentPage === 1"
              @click="currentPage--"
            >
              Sebelumnya
            </button>

            <span class="align-self-center small text-muted">Halaman {{ currentPage }} dari {{ totalPages }}</span>

            <button 
              class="btn btn-sm btn-outline-secondary ms-2"
              :disabled="currentPage === totalPages"
              @click="currentPage++"
            >
              Selanjutnya
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ratakanGalat } from '@/utils/galatForm';
import axios from 'axios';
import { useToast } from 'vue-toastification';

export default {
  name: 'ContingentRegistrationPage',

  setup() {
    // this.toast dipakai di beberapa blok catch. vue-toastification
    // hanya menyediakan this.$toast secara global, bukan this.toast.
    return { toast: useToast() };
  },
  data() {
    return {
      tournamentName: '',
      tournamentImage:'',
      tournamentLogo: '',
      tournamentDescription: '',
      tournamentDate: '',
      tournamentLocation: '',
      dateStart: '',
      dateEnd: '',
      document: '',
      matchCategories: [],
      ageCategories: [],
      activities: [],
      contingents: [],
      contingentList: [],
      currentPage: 1,          // ✅ halaman saat ini
      itemsPerPage: 5,         // ✅ jumlah kontingen per halaman
      formData: {
        person_responsible: '',
        email: '',
        password: '',
        password_confirmation: '',
      },
      errors: {},
      loading: false,
      progress: 0,
    };
  },

  computed: {
    paginatedContingents() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.contingentList.slice(start, start + this.itemsPerPage);
    },

    totalPages() {
      return Math.ceil(this.contingentList.length / this.itemsPerPage) || 1;
    },

    jumlahDipilih() {
      return (this.contingents || []).filter((c) => c.isSelected).length;
    }
  },

  watch: {
    contingentList() {
      this.currentPage = 1;
    }
  },

  created() {
    this.loadContingent(this.$route.params.id);
    this.fetchTournamentDetail(this.$route.params.id);
  },

  methods: {
    async fetchTournamentDetail(id) {
      this.loading = true;
      try {
        const response = await axios.get(`/tournaments/${id}`);
        if (response.data) {
          this.tournamentName = response.data.name;
          this.tournamentImage = response.data.image;
          this.tournamentLogo = response.data.logo_url || response.data.logo || '';
          this.tournamentDescription = response.data.description;
          this.tournamentDate = response.data.event_date || response.data.date;
          this.tournamentLocation = response.data.location;
          this.dateStart = response.data.start_date || response.data.date_start;
          this.dateEnd = response.data.end_date || response.data.date_end;
          this.matchCategories = response.data.categories || [];
          this.ageCategories = response.data.age_categories || [];
          this.document = response.data.document;
          this.activities = response.data.activities || [];
        }
      } catch (error) {
        useToast().error("Gagal memuat rincian turnamen.");
      } finally {
        this.loading = false;
      }
    },

    async loadContingent(selectedTournamentId) {
      this.loading = true;
      try {
        const response = await axios.get(
          '/contingents/my-contingents',
          {
            params: { tournament_id: selectedTournamentId },
            headers: {
              Authorization: `Bearer ${localStorage.getItem('authToken')}`
            }
          }
        );
        const data = response.data || [];
        this.contingents = data.map((c) => ({ ...c, isSelected: false }));
        this.contingentList = this.contingents;
        this.currentPage = 1;
      } catch (error) {
        console.error("Error loading contingents:", error);
      } finally {
        this.loading = false;
      }
    },

    async submitForm() {
      const selectedContingents = (this.contingents || [])
        .filter((contingent) => contingent.isSelected)
        .map((contingent) => contingent.id);

      if (selectedContingents.length === 0) {
        useToast().warning('Pilih setidaknya satu kontingen untuk didaftarkan.');
        return;
      }

      this.loading = true;
      this.errors = {};

      try {
        await axios.post(
          '/tournaments/register',
          {
            tournament_id: this.$route.params.id,
            contingents: selectedContingents,
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            },
          }
        );

        useToast().success('Kontingen berhasil didaftarkan!');
        this.loadContingent(this.$route.params.id);
      } catch (error) {
        if (error.response && error.response.status === 422) {
          this.errors = ratakanGalat(error.response.data.errors);
          useToast().error('Pilih kontingen yang akan diikutsertakan.');
        } else {
          useToast().error('Terjadi kesalahan saat mendaftar.');
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
/* ── Step Guide Card ── */
.registration-guide-card {
  background: var(--bg-surface);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-xl);
  padding: 1.25rem 1.5rem;
  box-shadow: var(--shadow-sm);
}

.guide-steps-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
}

.guide-step {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 0.75rem 1rem;
  background: var(--bg-sunken);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg);
  opacity: 0.7;
  transition: all var(--transition-fast);
}

.guide-step.is-active {
  background: var(--accent-subtle);
  border-color: rgba(240, 80, 8, 0.25);
  opacity: 1;
}

.guide-step__num {
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  font-size: 0.85rem;
  font-weight: 700;
  color: #fff;
  background: var(--text-muted);
  border-radius: 50%;
  flex-shrink: 0;
}

.guide-step.is-active .guide-step__num {
  background: var(--accent);
  box-shadow: 0 2px 6px rgba(240, 80, 8, 0.25);
}

.guide-step__text strong {
  display: block;
  font-size: 0.85rem;
  color: var(--text-primary);
}

.guide-step__text small {
  font-size: 0.72rem;
  color: var(--text-secondary);
  line-height: 1.2;
}

/* ── Tournament Info Card ── */
.tournament-info-card {
  background: var(--bg-surface);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-xl);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
}

.tournament-info-card__img {
  width: 100%;
  height: 240px;
  object-fit: cover;
  border-bottom: 1px solid var(--border-subtle);
}

.tournament-name {
  font-family: var(--font-sans);
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--text-primary);
}

.info-badges-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.info-badge-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.65rem 0.85rem;
  background: var(--bg-sunken);
  border-radius: var(--radius-md);
  font-size: 0.82rem;
}

.info-badge-item i { font-size: 1.25rem; flex-shrink: 0; }

/* ── Contingent Select Card ── */
.contingent-select-card {
  background: var(--bg-surface);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-xl);
  padding: 1.5rem;
  box-shadow: var(--shadow-sm);
}

.contingents-box-list {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.contingent-item-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.85rem 1.15rem;
  background: var(--bg-sunken);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg, 12px);
  transition: all var(--transition-fast);
}

.contingent-item-card:hover {
  border-color: var(--border-strong);
  background: var(--bg-surface);
}

.contingent-item-card.is-checked {
  background: var(--accent-subtle);
  border-color: var(--accent);
}

.contingent-item-card.is-registered {
  background: rgba(22, 163, 74, 0.05);
  border-color: rgba(22, 163, 74, 0.3);
}

.contingent-item-left {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  flex: 1;
}

.contingent-checkbox-wrap input {
  width: 20px;
  height: 20px;
  cursor: pointer;
}

.contingent-item-name {
  cursor: pointer;
  font-size: 0.92rem;
}

.button-sm {
  min-height: 34px !important;
  padding: 0.35rem 0.85rem !important;
  font-size: 0.78rem !important;
}
</style>
